// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable no-prototype-builtins */
import { CustomAppState } from "@bg/App/Store/Reducers";
import ProductContextAvailability from "@bg/Overrides/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@bg/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@bg/Overrides/Components/ProductImage";
import ProductPartNumbers from "@bg/Overrides/Components/ProductPartNumbers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { getUnitRegularPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    productInfo: ProductInfo;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    relatedProductQty: state.commonActions["relatedProductQty"]?.[ownProps.product.id],
});

export interface RelatedProductStyle {
    wrapper?: InjectableCss;
    rightSectionPrice?: InjectableCss;
    rightSectionQty?: InjectableCss;
    rightSectionTotal?: InjectableCss;
    lineWrapper?: InjectableCss;
    lineRightSection?: InjectableCss;
    something?: InjectableCss;
    lineLeftSection?: InjectableCss;
    productImageWrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    lineDetailsSection?: InjectableCss;
    descriptionWrapper?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    lineNumbersWrapper?: InjectableCss;
    lineAvaibility?: InjectableCss;
    pricingText?: TypographyPresentationProps;
}

export const styles: RelatedProductStyle = {
    rightSectionPrice: {
        css: css`
            width: calc((100% - 60px) / 2);
            text-align: left;
            padding-right: 5px;
            legend {
                display: none;
            }
            @media (max-width: 1023px) {
                legend {
                    display: block;
                }
            }

            @media print {
                legend {
                    display: block;
                }
            }
        `,
    },
    rightSectionQty: {
        css: css`
            width: 60px;
            text-align: center;
            &&& input {
                border-radius: 4px !important;
            }
        `,
    },
    rightSectionTotal: {
        css: css`
            width: calc((100% - 60px) / 2);
            text-align: right;
            legend {
                display: none;
            }
            @media (max-width: 1023px) {
                legend {
                    display: block;
                }
            }
            @media print {
                legend {
                    display: block;
                }
            }
        `,
    },
    lineWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 15px 10px;
            border-bottom: 1px solid ${getColor("common.ui2")};
            @media print {
                border-bottom-width: 2px;
            }
        `,
    },
    lineLeftSection: {
        css: css`
            display: flex;
            flex-grow: 1;
            width: 100%;
            max-width: 620px;
        `,
    },
    lineRightSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            align-items: flex-start;
            @media (max-width: 1023px) {
                margin-top: 15px;
            }
        `,
    },
    productImageWrapper: {
        css: css`
            margin-right: 30px;
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                height: 114px;
                width: 114px;
            `,
        },
        image: {
            cloudinaryWidth: "114",
        },
    },
    lineDetailsSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: fontConfig.fontSize.large,
                lineHeight: fontConfig.lineHeight.small,
                css: css`
                    opacity: 0.8;
                    height: 44px;
                    margin-bottom: 10px;
                `,
            },
        },
        productDetailDescription: {
            size: fontConfig.fontSize.large,
            lineHeight: fontConfig.lineHeight.small,
            css: css`
                opacity: 0.8;
                height: 44px;
                margin-bottom: 10px;
            `,
        },
    },
    lineAvaibility: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex: 1;
        `,
    },
    something: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    pricingText: {
        size: fontConfig.fontSize.large,
        weight: fontConfig.weight.bold,
        lineHeight: fontConfig.lineHeight.small,
        css: css`
            margin-bottom: 0;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const RelatedProductItem: React.FC<Props> = ({ product, productInfo, relatedProductQty }) => {
    const [qty, setQty] = useState(relatedProductQty || productInfo.qtyOrdered);
    const dispatch = useAppDispatch();

    const updateQuantity = (qtyOrdered: number) => {
        const quantities = relatedProductQty ? { ...relatedProductQty } : {};
        if (quantities?.hasOwnProperty(product.id)) {
            quantities[product.id] = qtyOrdered;
        } else {
            quantities[product.id] = 0;
            quantities[product.id] = qtyOrdered;
        }

        setQty(qtyOrdered);
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "relatedProductQty",
                value: { ...quantities },
            },
        });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: () => {},
        onQtyOrderedChanged: updateQuantity,
    };

    const getItemTotal = useMemo(() => {
        let total = 0;
        if (productInfo && productInfo.pricing) {
            total =
                Math.round(getUnitRegularPrice(productInfo.pricing, productInfo.qtyOrdered).price * qty * 100) / 100;

            Number.isNaN(total) ? (total = 0) : null;
        }
        return total;
    }, [productInfo, qty]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.lineWrapper}>
                    <StyledWrapper {...styles.lineLeftSection}>
                        <StyledWrapper {...styles.productImageWrapper}>
                            <ProductImage product={productContext} extendedStyles={styles.productImage} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.lineDetailsSection}>
                            <ProductDescription
                                lines={2}
                                lineHeight={22}
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />

                            <StyledWrapper {...styles.lineNumbersWrapper}>
                                <ProductPartNumbers
                                    productNumber={product.detail?.sku || product.productNumber}
                                    manufacturerItem={product.manufacturerItem}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.lineAvaibility}>
                                <ProductContextAvailability />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...styles.lineRightSection}>
                        <StyledWrapper {...styles.something}>
                            <StyledWrapper {...styles.rightSectionPrice}>
                                <legend>{translate("Price/Unit")}</legend>
                                <Typography {...styles.pricingText}>
                                    <LocalizedCurrency amount={productInfo.pricing?.unitNetPrice || 0} />
                                    {productInfo.unitOfMeasure ? ` / ${productInfo.unitOfMeasure}` : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.rightSectionQty}>
                                <ProductQuantityOrdered
                                    labelOverride=""
                                    {...({ showAddRemoveButtons: false } as any)}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.rightSectionTotal}>
                                <legend>{translate("Total")}</legend>
                                <Typography {...styles.pricingText}>
                                    <LocalizedCurrency amount={getItemTotal} />
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </ProductContext.Provider>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(RelatedProductItem);
